import { Controller } from '@hotwired/stimulus'
import { createConsumer } from '@rails/actioncable'

// Customer chat on the public ticket page. Server-rendered all the way: every
// signal (live broadcast, reconnect, own send) funnels into refresh(), which
// fetches the message stream fragment bounded by after_id and appends only what's
// new. The conversation may not exist on page load (created lazily by the first
// message) — we subscribe as soon as a fragment brings back a conversation id.
export default class extends Controller {
  static targets = ['scroll', 'input']
  static values = {
    ticketPublicId: String,
    messagesUrl: String,
  }

  connect() {
    this.subscribeIfPossible()
    this.scrollToBottom()
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
    if (this.consumer) this.consumer.disconnect()
  }

  // the visitor identity rides the cable URL as a query param — it's the same
  // low-privilege capability already present in the page URL
  subscribeIfPossible() {
    if (this.subscription || !this.conversationId) return

    this.consumer = this.consumer
      || createConsumer(`/cable?ticket_public_id=${encodeURIComponent(this.ticketPublicIdValue)}`)

    this.subscription = this.consumer.subscriptions.create(
      { channel: 'ConversationChannel', conversation_id: this.conversationId },
      {
        connected: () => this.refresh(), // covers reconnect gaps too
        received: envelope => this.received(envelope),
      },
    )
  }

  received(envelope) {
    if (envelope.v !== 1) return
    if (envelope.event === 'message.created') this.refresh()
  }

  async refresh() {
    const url = new URL(this.messagesUrlValue, window.location.origin)
    if (this.lastMessageId) url.searchParams.set('after_id', this.lastMessageId)

    const response = await fetch(url, { headers: { Accept: 'text/html' } })
    if (!response.ok) return

    this.appendFragment(await response.text())
    this.subscribeIfPossible() // the fragment may have brought a conversation id
  }

  // double-submit guard: disabled while the request is in flight (re-enabled in
  // finally), so rapid Enters can't duplicate
  async send(event) {
    event.preventDefault()
    const form = event.target
    const button = form.querySelector('[type="submit"]')
    if (button?.disabled) return

    if (button) button.disabled = true
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      })
      if (!response.ok) return

      form.reset()
      await this.refresh()
    } finally {
      if (button) button.disabled = false
    }
  }

  // parse the fragment, adopt its conversation id, append only unseen messages
  appendFragment(html) {
    const template = document.createElement('template')
    template.innerHTML = html
    const fragment = template.content.querySelector('.chat-stream')
    if (!fragment) return

    if (fragment.dataset.conversationId) {
      this.stream.dataset.conversationId = fragment.dataset.conversationId
    }

    fragment.querySelectorAll('[data-message-id], [data-day]').forEach(node => {
      if (node.dataset.day && this.stream.querySelector(`[data-day="${node.dataset.day}"]`)) return
      if (node.dataset.messageId && this.stream.querySelector(`[data-message-id="${node.dataset.messageId}"]`)) return

      this.stream.appendChild(node)
    })
    this.scrollToBottom()
  }

  get stream() {
    return this.scrollTarget.querySelector('.chat-stream')
  }

  get conversationId() {
    return this.stream.dataset.conversationId
  }

  get lastMessageId() {
    const nodes = this.stream.querySelectorAll('[data-message-id]')
    return nodes.length ? nodes[nodes.length - 1].dataset.messageId : null
  }

  scrollToBottom() {
    this.scrollTarget.scrollTop = this.scrollTarget.scrollHeight
  }
}
